"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  BarChart3,
  FileText,
  Globe,
  Shield,
  Clock,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const features = [
  {
    icon: CreditCard,
    title: "Gestión de cuentas bancarias",
    description:
      "Conecta y administra todas tus cuentas bancarias en un solo lugar, con sincronización automática de transacciones.",
  },
  {
    icon: BarChart3,
    title: "Seguimiento de ingresos y gastos",
    description:
      "Visualiza y categoriza automáticamente tus ingresos y gastos para entender tus patrones financieros.",
  },
  {
    icon: DollarSign,
    title: "Soporte para múltiples monedas",
    description:
      "Maneja soles, dólares y otras monedas con conversión automática al tipo de cambio actual.",
  },
  {
    icon: FileText,
    title: "Reportes automáticos",
    description:
      "Genera informes detallados de tu situación financiera, ideales para toma de decisiones y declaraciones de impuestos.",
  },
  {
    icon: Globe,
    title: "Optimizado para Perú",
    description:
      "Diseñado específicamente para el contexto financiero y tributario peruano, incluyendo facturación electrónica.",
  },
  {
    icon: Shield,
    title: "Seguridad avanzada",
    description:
      "Protección de datos de nivel bancario con encriptación de extremo a extremo y autenticación de dos factores.",
  },
  {
    icon: Clock,
    title: "Recordatorios de pagos",
    description:
      "Nunca olvides una fecha importante con alertas personalizables para facturas, impuestos y otros compromisos.",
  },
  {
    icon: Zap,
    title: "Conciliación inteligente",
    description:
      "Reconcilia automáticamente tus transacciones con facturas y recibos para mantener todo organizado.",
  },
];

export default function Features() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <section id="caracteristicas" className="py-20 relative">
      {/* Background gradient effect */}
      <div className="absolute -bottom-24 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-chart-4/10 rounded-full blur-3xl dark:bg-chart-4/5" />

      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Todas las herramientas que necesitas para
            <span className="bg-gradient-to-r from-chart-1 to-chart-2 text-transparent bg-clip-text block mt-1">
              gestionar tus finanzas
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Diseñamos finanza.lat para eliminar la complejidad de la gestión
            financiera y ayudarte a tomar mejores decisiones de negocio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full border-muted/40 bg-card/50 backdrop-blur transition-all duration-300 hover:bg-card hover:shadow-md hover:border-muted/80">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-chart-1/20 to-chart-2/20 flex items-center justify-center mb-3">
                    <feature.icon className="h-6 w-6 text-chart-1" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
